import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useInfiniteQuery, useMutation } from "react-query";
import { ErrorResponse, WebhookSubscription, WebhookSubscriptionsService } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { useDialogState } from "hooks/useDialogState";
import { LoadingButton } from "@mui/lab";
import { describeApplicationEventType } from "utils/applicationEventUtils";
import { CreateWebhookDialog } from "./CreateWebhookDialog";
import { EditWebhookDialog } from "./EditWebhookDialog";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  clientId: string;
}

const WebhooksTab: React.FC<Props> = ({ clientId }) => {
  const isReadOnly = useClientReadOnly();
  const [isCreateDialogOpen, openCreateDialog, closeCreateDialog] = useDialogState();
  const [isEditDialogOpen, openEditDialog, closeEditDialog] = useDialogState();
  const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [selectedWebhook, setSelectedWebhook] = React.useState<WebhookSubscription | null>(null);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } = useInfiniteQuery<
    { items: WebhookSubscription[]; nextToken?: string },
    ErrorResponse
  >(
    ["listWebhookSubscriptions", clientId],
    ({ pageParam }) => WebhookSubscriptionsService.listWebhookSubscriptions({ clientId, pageToken: pageParam }),
    {
      getNextPageParam: lastPage => lastPage.nextToken,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const deleteMutation = useMutation(
    (subscriptionId: string) => WebhookSubscriptionsService.deleteWebhookSubscription({ subscriptionId }),
    {
      onSuccess: () => {
        refetch();
        closeDeleteDialog();
        setSelectedWebhook(null);
      }
    }
  );

  const handleEditClick = React.useCallback(
    (webhook: WebhookSubscription) => {
      setSelectedWebhook(webhook);
      openEditDialog();
    },
    [openEditDialog]
  );

  const handleDeleteClick = React.useCallback(
    (webhook: WebhookSubscription) => {
      setSelectedWebhook(webhook);
      openDeleteDialog();
    },
    [openDeleteDialog]
  );

  const handleCreateSuccess = React.useCallback(() => {
    refetch();
    closeCreateDialog();
  }, [refetch, closeCreateDialog]);

  const handleEditSuccess = React.useCallback(() => {
    refetch();
    closeEditDialog();
    setSelectedWebhook(null);
  }, [refetch, closeEditDialog]);

  const handleDeleteConfirm = React.useCallback(() => {
    if (selectedWebhook) {
      deleteMutation.mutate(selectedWebhook.id);
    }
  }, [selectedWebhook, deleteMutation]);

  const webhooks = React.useMemo(() => {
    const items = data?.pages.flatMap(page => page.items) || [];
    return [...items].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const columns: DataTableColumn[] = [
    {
      id: "name",
      title: <strong>Name</strong>,
      cell: (row: WebhookSubscription) => row.name
    },
    {
      id: "eventType",
      title: <strong>Event Type</strong>,
      cell: (row: WebhookSubscription) => describeApplicationEventType(row.eventType)
    },
    {
      id: "callbackUrl",
      title: <strong>Callback URL</strong>,
      cell: (row: WebhookSubscription) => row.callbackUrl
    },
    {
      id: "notes",
      title: <strong>Notes</strong>,
      cell: (row: WebhookSubscription) => row.notes || "—"
    }
  ];

  const rowActions = React.useCallback(
    (row: WebhookSubscription): DataTableRowAction[] => [
      { type: "ACTION", title: "Edit", action: () => handleEditClick(row), disabled: isReadOnly },
      {
        type: "ACTION",
        title: "Delete",
        action: () => handleDeleteClick(row),
        color: "error.main",
        disabled: isReadOnly
      }
    ],
    [handleEditClick, handleDeleteClick, isReadOnly]
  );

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Webhooks</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} disabled={isReadOnly}>
          Add Webhook
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <ApiErrorMessage apiError={error} />
        </Box>
      )}

      <DataTable
        data={webhooks}
        rowKey={(row: WebhookSubscription) => row.id}
        columns={columns}
        rowActions={rowActions}
        isLoading={isLoading}
        dense
        noData={
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No webhooks found. Click "Add Webhook" to create one.
            </Typography>
          </Box>
        }
      />

      {hasNextPage && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <LoadingButton onClick={() => fetchNextPage()} loading={isFetchingNextPage} variant="outlined">
            Load more
          </LoadingButton>
        </Box>
      )}

      <CreateWebhookDialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        clientId={clientId}
        onSuccess={handleCreateSuccess}
      />

      {selectedWebhook && (
        <EditWebhookDialog
          open={isEditDialogOpen}
          onClose={closeEditDialog}
          webhook={selectedWebhook}
          onSuccess={handleEditSuccess}
        />
      )}

      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title="Delete Webhook"
          content="Are you sure you want to delete this webhook? This action cannot be undone."
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteConfirm}
          isLoading={deleteMutation.isLoading}
          error={deleteMutation.error}
          confirmationButtonColor="error"
        />
      )}
    </Box>
  );
};

export { WebhooksTab };
